import React, { useRef } from 'react';

import type { ChangeEvent, DetailedHTMLProps, FocusEvent, MouseEvent, Ref } from 'react';

import { ButtonSize } from '../../../types/variants';
import { isSafariBrowser } from '../../../utils/browser';
import cx from '../../../utils/classNames';
import toCamelCase from '../../../utils/toCamelCase';

import styles from '../textField.module.scss';

type Props = {
  /** Custom class to overwrite default styling if needed */
  className?: string;
  /** Controls the size of the textfield */
  size?: ButtonSize;
  /** The name of the input field */
  name?: string;
  /** Default value for the input field */
  defaultValue?: string;
  /** Current value of the input field */
  value?: string;
  /** Whether the input field is disabled */
  isDisabled?: boolean;
  /** Function to handle blur event */
  handleBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  /** Function to handle change event */
  handleChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Function to handle click event */
  handleClick?: (event: MouseEvent<HTMLInputElement>) => void;
  /** Optional ref to forward to the input element */
  forwardRef?: Ref<HTMLInputElement>;
  /** Standard HTML props */
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';
} & Omit<
  DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'size' | 'ref'
>;

const Input = (props: Props) => {
  const {
    className,
    size = ButtonSize.Medium,
    name,
    defaultValue,
    value,
    isDisabled = false,
    handleBlur,
    handleChange,
    handleClick,
    forwardRef,
    onKeyDown,
    ...rest
  } = props;

  const justEndedComposition = useRef(false);

  const handleCompositionEnd = () => {
    justEndedComposition.current = true;
  };

  const isSafari = isSafariBrowser();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // keyCode is used here to reliably detect the Enter key (keyCode 13) for input confirmation,
    // regardless of IME composition state or browser-specific quirks.
    if ('keyCode' in e) {
      if (e.keyCode === 13) {
        if (onKeyDown) onKeyDown(e);
        return;
      }
    } else {
      // // // Ignore keydown events that are part of an ongoing composition for IME input
      // // // This is particularly important for Safari, which does not set `isComposing` correctly
      if (isSafari && justEndedComposition.current) {
        justEndedComposition.current = false;

        return;
      }

      if (!isSafari && (e as React.KeyboardEvent<HTMLInputElement>).nativeEvent.isComposing) return;

      if (onKeyDown) onKeyDown(e);
    }
  };

  return (
    <input
      data-testid="textField-input"
      className={cx(
        styles[toCamelCase('textField', 'input')],
        styles[toCamelCase('textField', 'input', size)],
        className,
      )}
      name={name}
      {...(value !== undefined ? { value } : defaultValue !== undefined ? { defaultValue } : {})}
      disabled={isDisabled}
      onBlur={handleBlur}
      onChange={handleChange}
      onClick={handleClick}
      ref={forwardRef}
      onCompositionEnd={handleCompositionEnd}
      onKeyDown={handleKeyDown}
      {...rest}
    />
  );
};

export default Input;
